import Graphic from "@arcgis/core/Graphic";
import { useEffect, useState } from "react";
import { AppConfig, LayersConfig } from "../../AppConfig";
import FeatureFieldWithLabel from "./FeatureFieldWithLabel";
import { projectToLatLong } from "../../utils/arcgisUtils";
import { isKystverketFieldMissing } from "../../utils/autofillUtils";
import { NFSStatus } from "../../utils/nfsUtils";
import { useSelector } from "react-redux";
import type FeatureLayer from "@arcgis/core/layers/FeatureLayer";

interface FeatureViewerAttributesProps {
    feature: Graphic | null;
    isEditing: boolean;
    showAllAttributes: boolean;
    hideNonEditable?: boolean;
    canBeSentToFdv: boolean;
    secondaryFeature?: Graphic;
    setHasEditedAttributes: (hasEditedAttributes: boolean) => void;
}

const FeatureViewerAttributes = (props: FeatureViewerAttributesProps) => {
    const { feature, isEditing, showAllAttributes, hideNonEditable, canBeSentToFdv } = props;
    const [layerConfig, setLayerConfig] = useState<LayersConfig | undefined>(undefined);
    const [renderedFields, setRenderedFields] = useState<React.JSX.Element[] | null>(null);

    const hasUnsavedChanges = useSelector((state: any) => state.app.hasUnsavedChanges);

    const renderFields = async () => {
        const fields: React.JSX.Element[] = [];
        if (!feature) return null;

        const fieldOrder = layerConfig?.FieldOrder;
        const orderedFields: string[] = [];

        const allFields = Object.keys(feature.attributes);
        if (fieldOrder) {
            for (const field of fieldOrder) {
                if (allFields.includes(field) || field === "Lat" || field === "Long") {
                    orderedFields.push(field);
                }
            }
        }
        for (const field of allFields) {
            if (!orderedFields.includes(field)) {
                orderedFields.push(field);
            }
        }

        // Pre-calculate lat/long geometry once if needed
        let latlongGeometry = null;
        const needsLatLong = orderedFields.some((key) => key === "Lat" || key === "Long");
        if (needsLatLong && feature.geometry) {
            latlongGeometry = await projectToLatLong(feature.geometry);
        }

        // Important fields have not been defined by customer, only order of fields using 5 first as important
        const fieldsToRender = showAllAttributes ? orderedFields : orderedFields.slice(0, 5);

        const isReadonlyExternallyEditable = (field: string) => {
            if (field === "gis_obj_id" && feature.attributes["nfs_status"] === NFSStatus.PROJECTED) return true;
            // Region and rode stay readonly, but when an existing object is missing one the
            // editor offers a button to look it up. That button lives behind isEditing, so
            // the field has to be let through - only while the value is actually missing.
            if (isKystverketFieldMissing(field, feature.attributes[field])) return true;
            return false;
        };

        for (const key of fieldsToRender) {
            if (key === "lighthouseData") continue;
            if (layerConfig?.ExcludedFields?.includes(key) || AppConfig.FeatureEditor.ExcludedFields.includes(key))
                continue;

            // Feature is light we want to apply filtering on fields related to light color.
            if (
                (feature.layer as FeatureLayer).layerId === 0 &&
                [
                    "rekkevidde_hvit",
                    "rekkevidde_rod",
                    "rekkevidde_gronn",
                    "rekkevidde_gul",
                    "lysstyrke_hvit",
                    "lysstyrke_rod",
                    "lysstyrke_gronn",
                    "lysstyrke_gul",
                ].includes(key)
            ) {
                const lightColor = feature.attributes["lys_farge"];
                if (lightColor === 1 && !key.endsWith("_hvit")) continue;
                if (lightColor === 3 && !key.endsWith("_rod")) continue;
                if (lightColor === 4 && !key.endsWith("_gronn")) continue;
                if (lightColor === 6 && !key.endsWith("_gul")) continue;
            }

            if (key === "Lat" || key === "Long") {
                if (!latlongGeometry || latlongGeometry.type !== "point") continue;
                fields.push(
                    <FeatureFieldWithLabel
                        key={key}
                        attributeKey={key}
                        isEditing={false}
                        feature={
                            {
                                attributes: {
                                    [key]: key === "Lat" ? latlongGeometry.latitude : latlongGeometry.longitude,
                                },
                            } as Graphic
                        }
                        hideNonEditable={hideNonEditable}
                        canBeSentToFdv={props.canBeSentToFdv}
                        secondaryFeature={props.secondaryFeature}
                        setHasEditedAttributes={props.setHasEditedAttributes}
                    />
                );
            } else {
                fields.push(
                    <FeatureFieldWithLabel
                        key={key}
                        attributeKey={key}
                        isEditing={
                            layerConfig?.ReadonlyFields.includes(key)
                                ? isReadonlyExternallyEditable(key)
                                    ? isEditing
                                    : false
                                : isEditing
                        }
                        feature={feature}
                        hideNonEditable={hideNonEditable}
                        canBeSentToFdv={props.canBeSentToFdv}
                        secondaryFeature={props.secondaryFeature}
                        setHasEditedAttributes={props.setHasEditedAttributes}
                    />
                );
            }
        }

        return fields;
    };

    useEffect(() => {
        const layer = AppConfig.Layers.find((l) => l.Name === feature?.layer?.id);
        setLayerConfig(layer);
    }, [feature]);

    useEffect(() => {
        const updateFields = async () => {
            if (!feature || !layerConfig) {
                setRenderedFields(null);
                return;
            }
            const fields = await renderFields();
            setRenderedFields(fields);
        };
        updateFields();
    }, [feature, layerConfig, isEditing, showAllAttributes, hideNonEditable, canBeSentToFdv]);

    if (!feature) return null;

    return (
        <>
            {hasUnsavedChanges && isEditing && (
                <span className="error-message">Fullfør flytting før du endrer attributter</span>
            )}
            <div className={`feature-viewer-feature-attributes ${hasUnsavedChanges && isEditing ? "disabled" : ""}`}>
                {renderedFields}
                <div className="feature-viewer-footer"></div>
            </div>
        </>
    );
};

export default FeatureViewerAttributes;
